import { apiGatewayEndpoints } from '~/services/arcane-api-gateway/api-gateway-endpoints';
import { fastApiEndpoints } from '~/services/arcane-fast-api/fast-api-endpoints';

import { featureFlags } from '~/utils/featureFlags';

import { getClient } from './clients';

const FLAG = 'useFastApi';

/*
 * Typedefs mirror the FastAPI OpenAPI schema for
 * POST /v0/assist/{thread_id}/stream and POST /v0/assist/{thread_id}/commands/resume.
 */

/**
 * SSE event types the server can forward. The server default for resume is
 * ['references', 'lifecycle', 'tools', 'messages', 'custom:image_gen'].
 * @typedef {'values'|'updates'|'checkpoints'|'tasks'|'debug'|'messages'|'custom'|'custom:image_gen'|'tools'|'references'|'lifecycle'} StreamChannel
 */

/**
 * One filled-in field on a prompt card, keyed to the template's variable name.
 * @typedef {{ variable: string, value: string|string[] }} PromptCardVariable
 */

/**
 * Plain-text turn — the common case. Matches the backend's TextQuery.
 */
export class TextQuery {
  type = 'text';

  constructor(text) {
    this.text = text;
  }
}

/**
 * A quick-prompt/prompt-card turn. Matches the backend's PromptCardQuery — the backend
 * re-renders the template server-side from versionContentId + variables, so no rendered
 * text travels with this (see useConversations#processPromptCardData for where
 * versionContentId/variables/title come from). `title` is sent so it's persisted and can be
 * read straight back on reload, instead of the reload path needing its own fetch for it.
 */
export class PromptCardQuery {
  type = 'promptCard';

  /**
   * @param {string} versionContentId
   * @param {PromptCardVariable[]} [variables]
   * @param {string} [title]
   */
  constructor(versionContentId, variables = [], title = '') {
    this.version_content_id = versionContentId;
    this.variables = variables;
    this.title = title;
  }
}

/** @typedef {TextQuery | PromptCardQuery} AssistQuery */

/**
 * Image generation params collected from the user by ImageGenParamsSSE.
 * @typedef {object} ImageGenerationParams
 * @property {number} [width=1024]
 * @property {number} [height=1024]
 * @property {string|null} brandStyleId
 * @property {string|null} remixImageId
 * @property {number} [numberOfImages=4] Range (0, 10].
 */

/**
 * Resume payload sent after the user fills in image style choices.
 * @typedef {{ type: 'imageStyleChoicesResponse', imageGenerationParams: ImageGenerationParams }} ImageStyleChoicesResponse
 */

/**
 * One human decision per pending tool call (HITLMiddleware).
 * @typedef {{ type: 'approve' }
 *   | { type: 'edit', editedAction: { name: string, args: Record<string, any> } }
 *   | { type: 'reject', message: string }
 *   | { type: 'respond', message: string }} HITLDecision
 */

/**
 * Resume payload for a HITLMiddleware tool-use interrupt.
 * @typedef {{ type: 'hitlResponse', decisions: HITLDecision[] }} HITLResponse
 */

/**
 * Discriminated union on `type`, matching the backend ResumeCommand.payload.
 * @typedef {ImageStyleChoicesResponse | HITLResponse} ResumeCommandPayload
 */

/**
 * Payload for POST /v0/assist/{thread_id}/commands/goto. Bypasses intent routing and
 * runs `goto` directly with a state update built from the given query/params. Works on
 * fresh threads and on threads whose previous run already completed (node re-entered,
 * not resumed).
 * @typedef {{ goto: 'image_generation', query?: AssistQuery|null, imageGenerationParams: ImageGenerationParams }} GotoCommandPayload
 */

/** @typedef {AsyncGenerator<{ event: string, data: object }>} SSEEventStream */

const endpoints = () => (featureFlags[FLAG] ? fastApiEndpoints.conversation : apiGatewayEndpoints.conversation);

// New-agent conversations (turns/events schema, see turn-schema-migration-plan.md) live under
// singular /v0/conversation, distinct from the legacy plural /v0/conversations above — still via
// the same flag-gated client, since a new-agent org is already expected to be on useFastApi too.
// New-agent conversation-list items are shaped { id, title, userId, createdAt, updatedAt } —
// ConversationList.vue/ConversationCard.vue/date-utils.js all expect `conversationId` and
// `timestamp` (the legacy list-item field names). Normalized once here so those components
// don't need to know which agent produced the list.
const normalizeNewAgentConversationListItem = (conversation) => ({
  ...conversation,
  conversationId: conversation.id,
  timestamp: conversation.updatedAt,
});

export const listConversations = async (useNewAgent = false) => {
  if (useNewAgent) {
    const response = await getClient(FLAG).get(fastApiEndpoints.conversation.newAgentConversations);
    return { ...response, data: (response.data || []).map(normalizeNewAgentConversationListItem) };
  }
  return getClient(FLAG).get(endpoints().userConversations);
};

export const fetchConversationById = (conversationId, useNewAgent = false) => {
  if (useNewAgent) {
    return getClient(FLAG).get(fastApiEndpoints.conversation.newAgentConversationById(conversationId));
  }
  return getClient(FLAG).get(endpoints().conversationById(conversationId));
};

/**
 * Stream an AI assist response for a conversation turn (POST /v0/assist/{thread_id}/stream).
 *
 * If the graph needs user input mid-run (image style choices, HITL tool review),
 * the stream ends early with an `interrupts` event; resume via resumeConversationSSE.
 * On normal completion a final `references` event is emitted.
 *
 * @param {string} conversationId Graph thread id.
 * @param {{ query: AssistQuery, filters?: object[], streamChannels?: StreamChannel[] }} data
 *   Request body — mirrors the backend's AssistRequest (query discriminated on `type`).
 * @returns {SSEEventStream} Falls back to a plain POST promise on the legacy gateway client.
 */
export const sendMessageSSE = (conversationId, data) => {
  const client = getClient(FLAG);
  const endpoint = fastApiEndpoints.assist.stream(conversationId);

  if (typeof client?.stream === 'function') {
    return client.stream(endpoint, data);
  }

  return client.post(endpoint, data);
};

/**
 * Resume a paused graph thread (POST /v0/assist/{thread_id}/commands/resume).
 *
 * Called after the stream emits an `interrupts` event; `payload` supplies the data
 * the graph paused to collect. Streaming behaviour is identical to sendMessageSSE.
 *
 * @param {string} conversationId Graph thread id.
 * @param {ResumeCommandPayload} payload Discriminated on `type`.
 * @returns {SSEEventStream}
 */
export const resumeConversationSSE = (conversationId, payload) => {
  const client = getClient(FLAG);
  const endpoint = fastApiEndpoints.assist.commandsResume(conversationId);
  return client.stream(endpoint, { payload });
};

/**
 * Jump straight to a graph node on a thread (POST /v0/assist/{thread_id}/commands/goto).
 *
 * Unlike stream/resume, this bypasses intent routing: `payload.goto` runs directly with
 * the state update built from `payload` (e.g. a remix query and image params). Streaming
 * behaviour is identical to sendMessageSSE.
 *
 * @param {string} conversationId Graph thread id.
 * @param {GotoCommandPayload} payload
 * @returns {SSEEventStream}
 */
export const gotoConversationSSE = (conversationId, payload) => {
  const client = getClient(FLAG);
  const endpoint = fastApiEndpoints.assist.commandsGoto(conversationId);
  return client.stream(endpoint, { payload });
};
